/*
  Pure half of Bugsnag error reporting: no `$` aliases, no browser globals and
  no Bugsnag import, so vite.config.ts and the Playwright runner can both load
  it. The runtime half is src/lib/errorReporting.ts.
*/

export type ReleaseStage = 'production' | 'preview' | 'development'

export const ReportingStages: ReleaseStage[] = ['production', 'preview']

/** Map Vercel's VERCEL_ENV to a Bugsnag release stage. Unset (local, Docker) is development. */
export const releaseStageFor = (vercelEnv: string | undefined): ReleaseStage => {
  if (vercelEnv === 'production' || vercelEnv === 'preview') return vercelEnv
  return 'development'
}

export interface ErrorReportingSettings {
  apiKey: string
  appVersion: string | undefined
  releaseStage: ReleaseStage
}

/** null means "off": no key, so nothing is loaded and nothing is sent. */
export const errorReportingSettings = (raw: {
  apiKey: string | undefined
  appVersion: string | undefined
  releaseStage: ReleaseStage
}): ErrorReportingSettings | null => {
  const apiKey = raw.apiKey?.trim()
  if (!apiKey) return null

  return { apiKey, appVersion: raw.appVersion || undefined, releaseStage: raw.releaseStage }
}

// Metadata keys Bugsnag replaces with [REDACTED] before sending.
export const RedactedKeys = [
  /email/i,
  /api_?key/i,
  /token/i,
  /secret/i,
  /password/i,
  /authorization/i
]

const EmailPattern = /[\w.+-]+(?:@|%40)[\w-]+(?:\.[\w-]+)*\.[a-z]{2,}/gi
const JwtPattern = /\beyJ[\w-]+\.[\w-]+\.[\w-]*/g
const SensitiveParamPattern =
  /([?&#;][^=&#\s]*(?:email|token|key|secret|password|code|auth)[^=&#\s]*=)[^&#\s]*/gi

/** Strip emails, JWTs and credential-looking query params out of free text and URLs. */
export const scrubString = (value: string): string =>
  value
    .replace(SensitiveParamPattern, '$1[REDACTED]')
    .replace(JwtPattern, '[REDACTED]')
    .replace(EmailPattern, '[email]')

/** scrubString applied to every string inside a breadcrumb or metadata object. */
export const scrubDeep = <T>(value: T): T => {
  if (typeof value === 'string') return scrubString(value) as T
  if (Array.isArray(value)) return value.map(scrubDeep) as T
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([key, inner]) => [key, scrubDeep(inner)])
    ) as T
  }
  return value
}
